import React, { useState, useEffect } from 'react';
import { Box, Chip, Popover, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

/** Package names auto-show below this count; double-click to expand at or above. */
const PACKAGE_LIST_THRESHOLD = 10;

/**
 * Renders a single compact warning chip in the header.
 * Clicking the chip opens a popover listing all individual issues.
 * Warnings with 10+ packages hide names until double-clicked.
 * Shows nothing when the array is empty.
 */
const DataQualityWarnings = ({ warnings = [] }) => {
  const [anchor, setAnchor] = useState(null);
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());

  useEffect(() => {
    if (!anchor) setExpandedKeys(new Set());
  }, [anchor]);

  const normalizedWarnings = warnings.map((warning) => {
    if (typeof warning === 'string') {
      return { message: warning, packages: [] };
    }
    return {
      message: warning?.message || '',
      packages: Array.isArray(warning?.packages) ? warning.packages : [],
    };
  });

  if (!normalizedWarnings.length) return null;

  const toggleExpanded = (key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleClosePopover = () => setAnchor(null);

  return (
    <>
      <Chip
        size="small"
        icon={
          <WarningAmberIcon
            fontSize="small"
            sx={{ color: '#92400e !important', fontSize: '14px !important' }}
          />
        }
        label={`${normalizedWarnings.length} data issue${normalizedWarnings.length !== 1 ? 's' : ''}`}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          bgcolor: '#fef3c7',
          color: '#92400e',
          border: '1px solid #fde68a',
          fontSize: '11px',
          fontWeight: 600,
          cursor: 'pointer',
          height: 26,
          '&:hover': { bgcolor: '#fde68a' },
        }}
      />

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            mt: 0.5,
            p: 1.5,
            maxWidth: 380,
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            border: '1px solid #fde68a',
            borderRadius: 2,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
          <WarningAmberIcon sx={{ color: '#d97706', fontSize: 16 }} />
          <Typography
            sx={{ fontSize: '12px', fontWeight: 700, color: '#92400e' }}
          >
            Data Quality Issues
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {normalizedWarnings.map((warning, i) => {
            const rowKey = `${warning.message}-${i}`;
            const { packages } = warning;
            const hasPackages = packages.length > 0;
            const isLargeList = packages.length >= PACKAGE_LIST_THRESHOLD;
            const isExpanded = expandedKeys.has(rowKey);
            const showPackageList = hasPackages && (!isLargeList || isExpanded);

            return (
              <Box key={rowKey}>
                <Box
                  onDoubleClick={() => {
                    if (isLargeList) toggleExpanded(rowKey);
                  }}
                  sx={{
                    ...(isLargeList && {
                      cursor: 'pointer',
                      borderRadius: 0.5,
                      mx: -0.5,
                      px: 0.5,
                      '&:hover': { bgcolor: 'rgba(253, 230, 138, 0.35)' },
                    }),
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '11px',
                      color: '#78350f',
                      pl: 1.5,
                      '&::before': { content: '"•"', mr: 0.75, ml: -1.5 },
                    }}
                  >
                    {warning.message}
                  </Typography>
                  {isLargeList && !isExpanded && (
                    <Typography
                      sx={{
                        fontSize: '10px',
                        color: '#b45309',
                        fontStyle: 'italic',
                        ml: 2.5,
                        mt: 0.25,
                      }}
                    >
                      Double-click to show {packages.length} packages
                    </Typography>
                  )}
                  {isLargeList && isExpanded && (
                    <Typography
                      sx={{
                        fontSize: '10px',
                        color: '#b45309',
                        fontStyle: 'italic',
                        ml: 2.5,
                        mt: 0.25,
                      }}
                    >
                      Double-click to hide packages
                    </Typography>
                  )}
                </Box>
                {showPackageList && (
                  <Box
                    sx={{
                      mt: 0.25,
                      ml: 2.5,
                      ...(isLargeList && {
                        maxHeight: 220,
                        overflowY: 'auto',
                        pr: 0.5,
                      }),
                    }}
                  >
                    {packages.map((pkg, idx) => (
                      <Typography
                        key={`${pkg}-${idx}`}
                        sx={{
                          fontSize: '10px',
                          color: '#92400e',
                          lineHeight: 1.3,
                        }}
                      >
                        {pkg}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Popover>
    </>
  );
};

export default DataQualityWarnings;
